import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { CompanyForm } from 'src/app/classes/form-groups/company-form';
import { ClientRegisteredVendorFee, Company } from 'src/app/classes/models';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-verify-business-checks-modal',
  templateUrl: './verify-business-checks-modal.component.html',
  styleUrl: './verify-business-checks-modal.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class VerifyBusinessChecksModalComponent implements OnInit {
  constructor(
    private activeModal: NgbActiveModal,
    private ToastService: ToastService
  ) {}

  @Input() Company?: Company;
  @Input() CompanyForm?: CompanyForm;

  standardFeeControl?: FormControl<number>;
  irsTinMatchFeeControl?: FormControl<number>;
  corporateFilingReportFeeControl?: FormControl<number>;

  standardFee?: ClientRegisteredVendorFee;
  irsTinMatchFee?: ClientRegisteredVendorFee;
  corporateFilingReportFee?: ClientRegisteredVendorFee;
  inputs?: {
    show: boolean;
    label: string;
    tooltip?: string;
    inputType: 'text' | 'checkBox';
    formControl?: string;
    control?: any;
  }[];

  close() {
    this.activeModal.dismiss('Cross Click');
  }

  save() {
    if (!this.Company) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot save. No company info found.',
        { returnError: true }
      );
    }

    if (!this.CompanyForm) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot save. No company form found.',
        { returnError: true }
      );
    }

    const fees: ClientRegisteredVendorFee[] = [];

    if (this.standardFeeControl && this.standardFee) {
      fees.push(
        Object.assign(this.standardFee, {
          feeAmt: this.standardFeeControl.value
        })
      );
    }

    if (
      this.irsTinMatchFee &&
      this.Company.allowIRSTinMatchFee &&
      this.irsTinMatchFeeControl
    ) {
      fees.push(
        Object.assign(this.irsTinMatchFee, {
          feeAmt: this.irsTinMatchFeeControl.value
        })
      );
    }

    if (
      this.corporateFilingReportFee &&
      this.Company.allowCorporateFilingReportFee &&
      this.corporateFilingReportFeeControl
    ) {
      fees.push(
        Object.assign(this.corporateFilingReportFee, {
          feeAmt: this.corporateFilingReportFeeControl.value
        })
      );
    }

    Object.assign(
      this.Company,
      { ClientRegisteredVendorFees: fees },
      this.CompanyForm.getRawValue()
    );

    this.activeModal.close();
  }

  loadData() {
    if (!this.CompanyForm) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot load data. No company form found.',
        { returnError: true }
      );
    }

    this.inputs = [
      {
        show: true,
        label: 'Vet Client Registered Vendor?',
        tooltip:
          'VIVE to run business verification as vendors are entered into VIVE, ahead of vendor registering, typically for fraud screening',
        inputType: 'checkBox',
        formControl: 'allowAutomatedBusinessVerificationClientRegisteredVendors'
      },
      {
        show: true,
        label: 'Verify Business Only Service?',
        tooltip:
          'If client only wishes to vet businesses and no further compliance screening is needed. Vendors will not be allowed to register, all vendor data to be provided by client.',
        inputType: 'checkBox',
        formControl: 'verifyClientRegisteredVendorBusinessOnly'
      },
      {
        show: true,
        label: 'Allow IRS Tin Match Only Fee?',
        tooltip: 'Allow Tincheck Only Fee?',
        inputType: 'checkBox',
        formControl: 'allowIRSTinMatchFee'
      },
      {
        show: true,
        label: 'Allow Corporate Filing Report Fee?',
        tooltip: 'Allow Corporate Filing Report Fee?',
        inputType: 'checkBox',
        formControl: 'allowCorporateFilingReportFee'
      },
      {
        show: true,
        label: 'Credit Client?',
        inputType: 'checkBox',
        tooltip:
          'Credits the Client when a Vendor Claims a Business Verified Vendor.',
        formControl: 'creditClientRegisteredVendor'
      },
      {
        show: true,
        label: 'SOS Search',
        inputType: 'text',
        tooltip:
          'Fee to charge client if client is to be charged for vetting client registered vendors.',
        formControl: 'clientRegisteredVendorStandardFee'
      },
      {
        show: this.CompanyForm.controls.allowIRSTinMatchFee.value,
        label: 'IRS Tin Match Only Fee',
        inputType: 'text',
        tooltip:
          'Fee to charge client vetting client registered vendors with SSN.',
        formControl: 'clientRegisteredVendorIrsTinMatchFee'
      },
      {
        show: this.CompanyForm.controls.allowCorporateFilingReportFee.value,
        label: 'SOS Report',
        inputType: 'text',
        tooltip:
          'Fee to charge client when running Corporate Filing Report on client registered vendors',
        formControl: 'clientRegisteredVendorCorporateFilingReportFee'
      }
    ];
  }

  setClientRegisteredFeeAmounts() {
    if (!this.Company) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot set ClientRegisteredVendorFee Amounts. No company info found.',
        { returnError: true }
      );
    }

    if (!this.CompanyForm) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot set ClientRegisteredVendorFee Amounts. No company form found.',
        { returnError: true }
      );
    }

    const { ClientRegisteredVendorFees } = this.Company;

    this.standardFee = new ClientRegisteredVendorFee({
      feeAmt: 0,
      feeType: 'STANDARD'
    });

    this.irsTinMatchFee = new ClientRegisteredVendorFee({
      feeAmt: 0,
      feeType: 'IRS TIN MATCH'
    });

    this.corporateFilingReportFee = new ClientRegisteredVendorFee({
      feeAmt: 0,
      feeType: 'CORPORATE FILING REPORT'
    });

    if (ClientRegisteredVendorFees && ClientRegisteredVendorFees.length > 0) {
      const standardFee = ClientRegisteredVendorFees.find(
        (fee) => fee.feeType === 'STANDARD'
      );
      const irsTinMatchFee = ClientRegisteredVendorFees.find(
        (fee) => fee.feeType === 'IRS TIN MATCH'
      );
      const corporateFilingReportFee = ClientRegisteredVendorFees.find(
        (fee) => fee.feeType === 'CORPORATE FILING REPORT'
      );

      if (standardFee && this.standardFeeControl) {
        this.standardFeeControl.setValue(standardFee.feeAmt || 0);
        this.standardFee = standardFee;
      }

      if (irsTinMatchFee && this.irsTinMatchFeeControl) {
        this.irsTinMatchFeeControl.setValue(irsTinMatchFee.feeAmt || 0);
        this.irsTinMatchFee = irsTinMatchFee;
      }

      if (corporateFilingReportFee && this.corporateFilingReportFeeControl) {
        this.corporateFilingReportFeeControl.setValue(
          corporateFilingReportFee.feeAmt || 0
        );
        this.corporateFilingReportFee = corporateFilingReportFee;
      }
    }
  }

  ngOnInit(): void {
    if (this.CompanyForm) {
      this.CompanyForm.setControl(
        'clientRegisteredVendorStandardFee',
        new FormControl<number>(0)
      );
      this.CompanyForm.setControl(
        'clientRegisteredVendorIrsTinMatchFee',
        new FormControl<number>(0)
      );
      this.CompanyForm.setControl(
        'clientRegisteredVendorCorporateFilingReportFee',
        new FormControl<number>(0)
      );

      this.standardFeeControl =
        this.CompanyForm.controls.clientRegisteredVendorStandardFee;
      this.irsTinMatchFeeControl =
        this.CompanyForm.controls.clientRegisteredVendorIrsTinMatchFee;
      this.corporateFilingReportFeeControl =
        this.CompanyForm.controls.clientRegisteredVendorCorporateFilingReportFee;

      this.setClientRegisteredFeeAmounts();

      this.CompanyForm.valueChanges.subscribe({
        next: () => this.loadData()
      });
    }

    this.loadData();
  }
}
